import { useCallback, useMemo } from 'react';

import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { UseQueryResult, useQuery } from 'react-query';
import { PackageUtilizationReport } from 'types';
import { client } from 'utils/api-client';

type UsePackageUtilizationParams = {
  period: string;
  startDate: Date | null;
  endDate: Date | null;
  athleteIds?: string[] | null;
  packageIds?: string[] | null;
  eventTypeIds?: string[] | null;
  utilizationRateRange?: [number, number] | null;
  groupByAthlete: boolean;
  groupByPackage: boolean;
  enabled?: boolean;
  csvExport?: string;
};

const PACKAGE_UTILIZATION_REPORT_QUERY_KEY = 'reports-utilization-package-utilization-report';

function usePackageUtilization(
  params: UsePackageUtilizationParams,
): UseQueryResult<AxiosResponse<PackageUtilizationReport[]>> {
  const {
    period,
    startDate,
    endDate,
    athleteIds,
    packageIds,
    eventTypeIds,
    utilizationRateRange,
    groupByAthlete = false,
    groupByPackage = false,
    enabled = true,
    csvExport,
  } = params;

  const fetchPackageUtilization = useCallback(() => {
    const query = new URLSearchParams();
    query.append('period', period);
    query.append('start_date', startDate ? dayjs(startDate).format('YYYY-MM-DD') : '');
    query.append('end_date', endDate ? dayjs(endDate).format('YYYY-MM-DD') : '');

    athleteIds?.forEach(id => query.append('athlete_ids', id));
    packageIds?.forEach(id => query.append('package_ids', id));
    eventTypeIds?.forEach(id => query.append('event_type_ids', id));

    query.append('group_by_athlete', String(groupByAthlete));
    query.append('group_by_package', String(groupByPackage));

    return client.get('/reports/utilization/package-utilization', { params: query });
  }, [period, startDate, endDate, athleteIds, packageIds, eventTypeIds, groupByAthlete, groupByPackage]);

  const queryResult = useQuery({
    queryKey: [
      PACKAGE_UTILIZATION_REPORT_QUERY_KEY,
      period,
      startDate,
      endDate,
      athleteIds,
      packageIds,
      eventTypeIds,
      groupByAthlete,
      groupByPackage,
      csvExport,
    ],
    queryFn: fetchPackageUtilization,
    enabled,
  });

  const filteredData = useMemo(() => {
    if (!queryResult.data?.data || !utilizationRateRange) {
      return queryResult.data;
    }

    const [minRate, maxRate] = utilizationRateRange;
    const filteredRows = queryResult.data.data.filter(
      (report: PackageUtilizationReport) =>
        report.utilizationRate !== null &&
        report.utilizationRate >= minRate / 100 &&
        report.utilizationRate <= maxRate / 100,
    );

    return {
      ...queryResult.data,
      data: filteredRows,
    };
  }, [queryResult.data, utilizationRateRange]);

  return {
    ...queryResult,
    data: filteredData,
  } as UseQueryResult<AxiosResponse<PackageUtilizationReport[]>>;
}

export default usePackageUtilization;
export { PACKAGE_UTILIZATION_REPORT_QUERY_KEY };
